import React from 'react';
import { motion } from 'framer-motion';
import {
  FaBell,
  FaCodeBranch,
  FaCube,
  FaDatabase,
  FaFingerprint,
  FaMicrochip,
  FaNetworkWired,
  FaRoute,
  FaSatelliteDish
} from 'react-icons/fa';
import '../css/PortalShowcase.css';

const panels = [
  { title: 'Gateway', meta: 'CAN routing', icon: <FaCodeBranch /> },
  { title: 'AI Core', meta: 'Decision engine', icon: <FaMicrochip /> },
  { title: 'Data Mesh', meta: 'Live signal map', icon: <FaDatabase /> },
  { title: 'Security', meta: 'Auth layer', icon: <FaFingerprint /> }
];

const nodes = [
  'CAN RX',
  'UDS',
  'AUTH',
  'API',
  'MOBILE',
  'SYNC'
];

const dataStreams = [
  '0x7E0 03 22 F1 90',
  '0x7E8 10 14 62 F1 90',
  '0x280 4A 12 00 FF',
  '0x3C0 01 00 20 AA',
  '0x19 DTC SNAPSHOT',
  '0x27 SEED REQUEST'
];

const shards = Array.from({ length: 12 }, (_, index) => index + 1);

export default function PortalShowcase() {
  return (
    <section className="portal-section" aria-label="Animated interface showcase">
      <div className="portal-backdrop" />
      <div className="portal-grid" />
      <div className="portal-aurora portal-aurora-one" />
      <div className="portal-aurora portal-aurora-two" />

      <div className="portal-content">
        <motion.div
          className="portal-copy"
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7 }}
        >
          <span className="portal-kicker">AI-built motion system</span>
          <h2>Not a template. A living interface scene for technical products.</h2>
          <p>
            Layered depth, signal streams, moving gateway logic, security states,
            and floating diagnostics turn software projects into a cinematic
            system people can feel in seconds.
          </p>

          <div className="portal-mini-stack" aria-label="Motion stack">
            <span>React</span>
            <span>Framer Motion</span>
            <span>CSS 3D</span>
            <span>Signal UI</span>
          </div>
        </motion.div>

        <motion.div
          className="portal-stage"
          initial={{ opacity: 0, scale: 0.94, rotateX: 8 }}
          whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <div className="portal-depth">
            <div className="portal-floor" />
            <div className="portal-light-beam beam-one" />
            <div className="portal-light-beam beam-two" />
            <div className="portal-light-beam beam-three" />

            <div className="portal-data-rain" aria-hidden="true">
              {dataStreams.map((stream, index) => (
                <span key={stream} className={`stream stream-${index + 1}`}>
                  {stream}
                </span>
              ))}
            </div>

            <div className="portal-orb">
              <div className="portal-energy portal-energy-one" />
              <div className="portal-energy portal-energy-two" />
              <div className="portal-energy portal-energy-three" />
              <div className="portal-energy portal-energy-four" />
              <div className="portal-ring portal-ring-a" />
              <div className="portal-ring portal-ring-b" />
              <div className="portal-ring portal-ring-c" />
              <div className="portal-ring portal-ring-d" />
              <div className="portal-shards" aria-hidden="true">
                {shards.map((shard) => (
                  <span key={shard} className={`portal-shard shard-${shard}`} />
                ))}
              </div>
              <div className="portal-core">
                <FaCube />
                <span>NEURAL</span>
              </div>
            </div>

            <div className="portal-node-orbit">
              {nodes.map((node, index) => (
                <span key={node} className={`portal-node node-${index + 1}`}>
                  {node}
                </span>
              ))}
            </div>

            <div className="portal-panels">
              {panels.map((panel, index) => (
                <motion.article
                  key={panel.title}
                  className={`portal-panel panel-${index + 1}`}
                  animate={{
                    y: [0, -12, 0],
                    rotateY: [-5, 5, -5],
                    boxShadow: [
                      '0 20px 55px rgba(0,0,0,0.28)',
                      '0 24px 75px rgba(34,211,238,0.22)',
                      '0 20px 55px rgba(0,0,0,0.28)'
                    ]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 5 + index * 0.7,
                    ease: 'easeInOut'
                  }}
                >
                  <div className="panel-icon">{panel.icon}</div>
                  <div>
                    <strong>{panel.title}</strong>
                    <span>{panel.meta}</span>
                  </div>
                </motion.article>
              ))}
            </div>

            <motion.div
              className="portal-route-card"
              animate={{ y: [0, 10, 0], rotateX: [3, -3, 3] }}
              transition={{ repeat: Infinity, duration: 5.8, ease: 'easeInOut' }}
            >
              <FaRoute />
              <div>
                <strong>ROUTING MAP</strong>
                <span>OBD -> Gateway -> ECU</span>
              </div>
              <div className="route-dots">
                <i />
                <i />
                <i />
              </div>
            </motion.div>

            <motion.div
              className="portal-notification"
              animate={{ x: [0, 14, 0], opacity: [0.82, 1, 0.82] }}
              transition={{ repeat: Infinity, duration: 4.2, ease: 'easeInOut' }}
            >
              <FaBell />
              <div>
                <strong>INTELLIGENT ALERT</strong>
                <span>New signal pattern detected</span>
              </div>
            </motion.div>

            <div className="portal-signal-card">
              <FaSatelliteDish />
              <span>Signal lock</span>
              <strong>98%</strong>
            </div>

            <div className="portal-brain-card">
              <FaNetworkWired />
              <div className="brain-bars">
                <span />
                <span />
                <span />
                <span />
              </div>
              <strong>Adaptive UI</strong>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
